/**
 * Units earned, per family
 *
 * ONE ENGINE: the eligibility engine, one sentence per shift.
 * READ BY: The family portal · the chair's roster · the statement · the credit run · the 2026
 * fulfillment part · the fulfillment statement · the school's Excel.
 * PROVEN AGAINST: The posted credit run, superseded but never edited.
 *
 * Every reader names this engine in small type, or shows NO DATA. Nothing else computes it.
 *
 * The rules are a year's own, set by a director per kind of shift and never per person (James,
 * Sep 25): how many credits fulfill a family's commitment, and what earns one —
 *
 *   · **a shift**: 1.0's math, and the default. A four-hour block is one credit, so an eight-hour
 *     shift is two. Working it earns it; leaving early does not cut it in half.
 *   · **hours**: hours toward a credit. Recruitment's four hours are one credit; a one-hour Mass
 *     shift is a quarter of one. What is earned is the hours actually worked, capped at the
 *     shift's own length — nobody earns more by forgetting to check out.
 *   · **nothing**: the kind does not count, and every surface says so in those words.
 *
 * Credit is a consequence of the check-out (rule B.3): this engine turns one check-out into
 * quarters of a credit, and the family's standing is the sum of what it has been given.
 *
 * A disputed year stands on its ruling (F17, ADR 0104): `standing()` takes it as its third input
 * and says so in the sentence. Nothing else applies a ruling, and a dispute not ruled yet
 * overrides nothing.
 */
import { NotImplemented } from './types.js';

/** What a kind of shift earns. */
export type Earns = 'shift' | 'hours' | 'nothing';

/** One kind of shift, as a director sets it for the year. */
export interface KindRule {
  /** The kind on the shift template — "general", "recruitment", "mass", the area's own word. */
  kind: string;
  earns: Earns;
  /**
   * The hours in one credit. On `shift` it is the block — four hours, so eight earn two. On
   * `hours` it is the divisor — four hours to a credit, so one hour is a quarter.
   */
  hoursPerCredit: number;
}

export interface CommitmentRules {
  festivalYear: number;
  /** How many credits fulfill a family's commitment. Two, unless a director says otherwise. */
  creditsToFulfill: number;
  kinds: KindRule[];
  /** The kind a shift with no kind of its own is read as. */
  defaultKind: string;
}

/** The year as it stands until a director sets it: 1.0's math, two credits, four-hour blocks. */
export const DEFAULT_RULES = (festivalYear: number): CommitmentRules => ({
  festivalYear,
  creditsToFulfill: 2,
  defaultKind: 'general',
  kinds: [{ kind: 'general', earns: 'shift', hoursPerCredit: 4 }],
});

export const QUARTERS = 4;

/** The rule for a kind, or the year's default kind, or 1.0's block. */
export function commitmentRuleFor(rules: CommitmentRules, kind: string | null): KindRule {
  const named = rules.kinds.find((k) => k.kind === (kind ?? rules.defaultKind));
  return (
    named ??
    rules.kinds.find((k) => k.kind === rules.defaultKind) ?? {
      kind: rules.defaultKind,
      earns: 'shift',
      hoursPerCredit: 4,
    }
  );
}

const quarters = (credits: number): number => Math.round(credits * QUARTERS);

/** "1¼", "2", "¼" — quarters as a person reads them. */
export function saysCredits(q: number): string {
  const whole = Math.floor(q / QUARTERS);
  const part = ((q % QUARTERS) + QUARTERS) % QUARTERS;
  const fraction = part === 0 ? '' : part === 1 ? '¼' : part === 2 ? '½' : '¾';
  if (whole === 0 && fraction === '') return '0';
  if (whole === 0) return fraction;
  return `${String(whole)}${fraction}`;
}

export interface WorkedShift {
  kind: string | null;
  /** The shift's own length, from its start and end. */
  scheduledHours: number;
  /** What the check-in and the check-out say was actually worked. */
  workedHours: number;
}

/**
 * What one check-out earns, in quarters of a credit. Nothing is earned without a check-out —
 * the caller only asks once the check-out is written.
 */
export function creditForCheckOut(rules: CommitmentRules, shift: WorkedShift): number {
  const rule = commitmentRuleFor(rules, shift.kind);
  if (rule.earns === 'nothing' || rule.hoursPerCredit <= 0) return 0;
  const worked = Math.max(0, Math.min(shift.workedHours, shift.scheduledHours));
  if (rule.earns === 'shift') {
    // 1.0's math: the block earns the credit, and a longer shift earns its blocks. Working it is
    // what earns it, so a short check-out still earns the shift it was signed up for.
    if (worked <= 0) return 0;
    return quarters(Math.max(1, Math.round(shift.scheduledHours / rule.hoursPerCredit)));
  }
  return quarters(worked / rule.hoursPerCredit);
}

/**
 * Standby time (the booth, 7f — "their standby time is still credited"): the wait from standby to
 * sent or released counts toward the commitment by the year's general rule, hour for hour — a
 * wait is time given, never a whole shift's block.
 */
export function creditForStandby(rules: CommitmentRules, waitedHours: number): number {
  const rule = commitmentRuleFor(rules, null);
  if (rule.earns === 'nothing' || rule.hoursPerCredit <= 0 || waitedHours <= 0) return 0;
  return quarters(waitedHours / rule.hoursPerCredit);
}

/** The line every shift surface carries, always — the builder cell, the roster, the family page. */
export function countsTowardLine(rules: CommitmentRules, kind: string | null): string {
  const rule = commitmentRuleFor(rules, kind);
  if (rule.earns === 'nothing') return 'Does not count toward the family commitment.';
  const hours = rule.hoursPerCredit;
  const each = hours === 1 ? '1 hour' : `${String(hours)} hours`;
  return rule.earns === 'shift'
    ? `Counts toward the family commitment — a ${String(hours)}-hour shift = 1 credit`
    : `Counts toward the family commitment — ${each} = 1 credit`;
}

/** The word a ruling gives a disputed year (ADR 0104). */
export type CreditWord = 'full' | 'partial' | 'none';

/**
 * A family's ruling for the year, from `volunteers.dispute` (ADR 0104): the dispute as 1.0's file
 * numbers it (`9a`, `M`) and its credit word — null while the dispute is not ruled yet, which is
 * never read as none. A family holds one a year. Who ruled and the day stay with the reader that
 * prints them.
 */
export interface Ruling {
  disputeNo: string;
  creditResult: CreditWord | null;
}

export interface Standing {
  /** Quarters earned, and what fulfills the year. */
  earned: number;
  toFulfill: number;
  /** The credits fill the year, or a ruling of full completes it. */
  complete: boolean;
  /**
   * "1¼ of 2 — 3 more hours to go", or "2 of 2 — complete"; on a disputed year the ruling
   * follows — "¼ of 2 — complete · the claim in dispute 9a was ruled full", "0 of 2 — 8 more
   * hours to go · dispute 7 is not ruled yet".
   */
  says: string;
  /** The hours still to work at the year's own rate; null once it is complete. */
  hoursToGo: number | null;
  /** What the credits alone say — `complete` before any ruling. */
  computedComplete: boolean;
  /** The ruling handed in, its dispute and its word only; null where the family has none. */
  ruled: Ruling | null;
}

/**
 * A ruling's half of the sentence: the dispute by its number and its word — never a date or who
 * ruled, which a reader that prints them adds after it ("by <who> on <day>").
 */
export const rulingSays = ({ disputeNo, creditResult }: Ruling): string =>
  creditResult === null
    ? `dispute ${disputeNo} is not ruled yet`
    : `the claim in dispute ${disputeNo} was ruled ${creditResult}`;

/**
 * Where a family stands: what it has been given, against what the year asks — and, on a disputed
 * year, what the ruling made of it (ADR 0104). The ruling is the third input, and only here is
 * one applied:
 *
 *   · **full** completes the year, whatever the credits say;
 *   · **partial** keeps the computed figure — the word Partial is the status ladder's;
 *   · **none** keeps the computed figure, and the sentence says the claim was ruled none;
 *   · **no word yet** overrides nothing and is never read as none — the sentence says the dispute
 *     is not ruled yet.
 *
 * Without a ruling, every field and the sentence are what the credits alone say.
 */
export function standing(
  rules: CommitmentRules,
  earnedQuarters: number,
  ruling: Ruling | null = null,
): Standing {
  const toFulfill = quarters(rules.creditsToFulfill);
  const left = Math.max(0, toFulfill - earnedQuarters);
  const rate = commitmentRuleFor(rules, null).hoursPerCredit;
  const computedComplete = left === 0;
  const complete = computedComplete || ruling?.creditResult === 'full';
  const hoursToGo = complete ? null : Math.round((left / QUARTERS) * rate * 10) / 10;
  const front = `${saysCredits(earnedQuarters)} of ${saysCredits(toFulfill)}`;
  const distance = complete
    ? `${front} — complete`
    : `${front} — ${String(hoursToGo)} more hour${hoursToGo === 1 ? '' : 's'} to go`;
  return {
    earned: earnedQuarters,
    toFulfill,
    complete,
    hoursToGo,
    says: ruling ? `${distance} · ${rulingSays(ruling)}` : distance,
    computedComplete,
    // The dispute and its word, and nothing else a reader handed in with them: a row's finding
    // never travels on through the engine.
    ruled: ruling ? { disputeNo: ruling.disputeNo, creditResult: ruling.creditResult } : null,
  };
}

/** The whole answer, per family, is the sum of what each check-out gave. Not yet wired. */
export function unitsEarnedPerFamily(): never {
  throw new NotImplemented('Units earned, per family');
}
